import React, { useEffect, useState, useCallback, useRef, Suspense } from 'react'
import { Provider, useSelector } from 'react-redux'
import store from './redux/reduxstore'
import { ThemeWrapper, setTheme, LIGHT_THEME } from 'irmlib/themes'
import CoreApp from './components/Pages/CoreApp'
import { GlobalBusyCursor, InformationDialog, GeneralDialog, Toast, ScreenBlocker, openInformationDialog, SnackbarProvider } from 'irmlib/containers'
import SessionTimeoutWrapper, { SessionTimeoutDialogBody } from 'irmlib/components/Controls/SessionTimeoutWrapper'
import Refresh from './components/Pages/Refresh'
import { changeLocale, isLoggedIn } from './redux/global'
import { DEFAULT_LANGUAGE } from 'irmlib/common'
import { setTranslationService } from 'irmlib/multilanguage'
import getTranslations from './services/general/getTranslations'
import { recordKeystroke } from './common/keystrokemanager'
import './index.css'
import { registerAuthListener } from './components/Login/Body'
import { updateLogin } from './services/firestore/login'

// import { Suspense } from 'react'
import i18n from './i18n'
import localeContext from './LocaleContext'
import { t } from 'i18next'

// eslint-disable-next-line
function Loading () {
  return (
    <label>Loading...</label>
  )
}

/**
 * App sets up the CoreApp, redux store, global redux connected components and theming for the application.
 *
 * @returns {React.ReactElement} - React component
 */
const BaseApp = () => {
  const [languageValid, setLanguageValid] = useState(false)
  const wrapperRef = useRef(null)
  const loggedIn = useSelector(isLoggedIn)

  const [locale, setLocale] = useState(i18n.language)
  i18n.on('languageChanged', (lng) => setLocale(i18n.language))

  useEffect(() => {
    const doLanguage = async () => {
      registerAuthListener()
      setTranslationService(getTranslations)
      await changeLocale(DEFAULT_LANGUAGE)
      setTheme(LIGHT_THEME)
      // Allow the theme to completely propagate the app before turing on the CoreApp
      setTimeout(() => setLanguageValid(true), 1)
    }
    doLanguage()
    setInterval(updateTotalTime, 10000)
  }, [])

  const onKeyDown = useCallback(e => {
    recordKeystroke(e)
  }, [])

  
  const onStayLoggedIn = () => {
    wrapperRef.current.restartTimer()
  }

  const updateTotalTime = async () => {
    if (isLoggedIn()) {
      await updateLogin()
    }
  }

  const onExpired = () => {
    openInformationDialog({
      dividers: true,
      title: t('app.idleModalTitle'),
      noTranslateTitle: true,
      message: SessionTimeoutDialogBody,
      okText: 'app.remainLoggedIn',
      okButtonVariant: 'contained',
      onOk: onStayLoggedIn
    })
  }

  return (
    <div onKeyDown={onKeyDown}>
      <SessionTimeoutWrapper ref={wrapperRef} isLoggedIn={loggedIn} noTranslate={true} onExpired={onExpired}>
        <localeContext.Provider value={{ locale, setLocale }}>
          <SnackbarProvider>
            <Suspense fallback={<Loading />} >
              {languageValid ? <CoreApp /> : <></>}
              <GlobalBusyCursor />
              <InformationDialog />
              <GeneralDialog />
              <Toast />
              <ScreenBlocker />
            </Suspense>
          </SnackbarProvider>
        </localeContext.Provider>
      </SessionTimeoutWrapper>
    </div>
  )
}

const App = () =>
  <Provider store={store}>
    <ThemeWrapper>
      <Refresh/>
      <BaseApp />
    </ThemeWrapper>
  </Provider>

export default App